import { useEffect, useRef, useCallback } from 'react';

/**
 * Hook to manage browser (Web Notifications API) notifications.
 * Only fires when the tab is not focused (background).
 */
export const useBrowserNotifications = () => {
  const permissionRef = useRef<NotificationPermission>(
    typeof Notification !== 'undefined' ? Notification.permission : 'denied'
  );

  // Sync permission ref if it changed externally
  useEffect(() => {
    if (typeof Notification !== 'undefined') {
      permissionRef.current = Notification.permission;
    }
  }, []);

  const sendBrowserNotification = useCallback(
    (title: string, options?: { body?: string; tag?: string; data?: { url?: string } }) => {
      // Only send when tab is NOT focused
      if (document.hasFocus()) return;

      if (typeof Notification === 'undefined') return;
      if (permissionRef.current !== 'granted') return;

      try {
        const notification = new Notification(title, {
          body: options?.body,
          tag: options?.tag, // prevents duplicate notifications with same tag
          icon: '/favicon.ico',
        });

        // Navigate on click if a URL is provided
        if (options?.data?.url) {
          notification.onclick = () => {
            window.focus();
            window.location.href = options.data!.url!;
            notification.close();
          };
        } else {
          notification.onclick = () => {
            window.focus();
            notification.close();
          };
        }
      } catch (e) {
        // Silently fail – some browsers restrict Notification in certain contexts
        console.warn('[BrowserNotifications] Failed to create notification:', e);
      }
    },
    []
  );

  const requestPermission = useCallback(async () => {
    if (typeof Notification === 'undefined') return 'denied' as NotificationPermission;
    const perm = await Notification.requestPermission();
    permissionRef.current = perm;
    return perm;
  }, []);

  return {
    sendBrowserNotification,
    requestPermission,
    permission: permissionRef.current,
  };
};
